/**
 * The application shell: workspaces, boards, and the board itself.
 *
 * ## Why the shell owns workspace loading and the board owns itself
 *
 * Workspaces and the board list are cheap, rarely changing, and shared across every
 * board - they belong to the shell. A board's state is a socket, a snapshot and an
 * optimistic overlay - it belongs to `BoardProvider`, which mounts and unmounts
 * with the route. The split is what makes switching boards a clean teardown: the
 * provider's effect cleanup closes the socket, and the new board opens its own.
 *
 * ## Why the workspace is derived from the route rather than from a fetch
 *
 * `#/w/<slug>/b/<id>` names the workspace. A user with two workspaces who switches
 * between them keeps two history entries and can bookmark either board. Resolving
 * the workspace from the board id instead would work and would make every board URL
 * indistinguishable from a bare `#/` until the fetch returned.
 *
 * ## The role comes from the workspace membership, not the board
 *
 * There is no per-board sharing: membership in the workspace is the only thing that
 * grants access to its boards. `role` here is that membership role, and it is what
 * `can()` is checked against for hiding controls. The server checks the same thing
 * again, from the database, on every request - the client's copy is a UI
 * convenience and never a control.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Board, Role, WorkspaceWithMembership } from '@slate/shared';
import { can } from '@slate/shared';
import { useAuth } from '../state/AuthProvider.js';
import { BoardProvider } from '../state/BoardProvider.js';
import { ApiError } from '../api/client.js';
import { navigate, useRoute } from '../lib/router.js';
import { Avatar } from './Avatar.js';
import { BoardView } from './BoardView.js';
import { Icon, Modal, Spinner, ghostButtonClass, inputClass, primaryButtonClass } from './ui.js';
import { useTheme } from '../state/ThemeProvider.js';

export function WorkspaceShell(): ReactNode {
  const { user, client, getAccessToken, logout } = useAuth();
  const route = useRoute();
  const { preference, setPreference } = useTheme();

  const [workspaces, setWorkspaces] = useState<WorkspaceWithMembership[] | null>(null);
  const [boards, setBoards] = useState<Board[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creatingBoard, setCreatingBoard] = useState(false);

  const activeWorkspace = useMemo(() => {
    if (workspaces === null) return null;
    if (route.workspaceSlug === null) return workspaces[0] ?? null;
    return workspaces.find((workspace) => workspace.slug === route.workspaceSlug) ?? null;
  }, [workspaces, route.workspaceSlug]);

  // Load the workspace list once. It changes only when a workspace is created or
  // the user is added to one, both of which are user-initiated and refresh it
  // explicitly.
  useEffect(() => {
    let cancelled = false;
    void client
      .listWorkspaces()
      .then((response) => {
        if (cancelled) return;
        setWorkspaces(response.workspaces);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.detail : 'Your workspaces could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  // The member list is deliberately not fetched here. The board snapshot carries
  // it (`state.members` in `BoardProvider`), and the shell has nothing to show
  // that the board's own assignee picker and presence stack do not - so a
  // workspace-detail request at this level would be a second copy of the same
  // data, one that could disagree with the board's.

  const loadBoards = useCallback(async (): Promise<void> => {
    if (activeWorkspace === null) return;
    try {
      const response = await client.listBoards(activeWorkspace.slug);
      setBoards(response.boards);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : 'Boards could not be loaded.');
      setBoards([]);
    }
  }, [client, activeWorkspace]);

  useEffect(() => {
    void loadBoards();
  }, [loadBoards]);

  // Land on a board when the route names a workspace but no board. Doing it here
  // rather than in the click handler means a bookmarked workspace URL also lands
  // somewhere useful.
  useEffect(() => {
    if (route.boardId !== null || boards === null || boards.length === 0 || activeWorkspace === null) return;
    navigate({ workspaceSlug: activeWorkspace.slug, boardId: boards[0]?.id ?? null });
  }, [route.boardId, boards, activeWorkspace]);

  const role: Role = activeWorkspace?.role ?? 'viewer';

  if (user === null) return null;

  return (
    <div className="flex h-full min-h-0">
      <nav aria-label="Workspaces and boards" className="flex w-60 shrink-0 flex-col border-r border-border bg-surface">
        <div className="border-b border-border px-3 py-3">
          <p className="text-sm font-semibold tracking-tight text-ink">Slate</p>
          <div className="mt-2 flex items-center gap-2">
            <Avatar name={user.name} colour={user.colour} size={24} title={user.name} />
            <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">{user.name}</span>
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Sign out"
              title="Sign out"
              className="rounded p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
            >
              <Icon name="logout" size={14} />
            </button>
          </div>
        </div>

        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {workspaces === null ? (
            <p className="px-2 py-3 text-xs text-ink-subtle">
              <Spinner label="Loading workspaces" />
            </p>
          ) : workspaces.length === 0 ? (
            <p className="px-2 py-3 text-xs text-ink-subtle">
              You are not a member of any workspace yet.
            </p>
          ) : (
            <>
              <label htmlFor="workspace-select" className="sr-only">
                Workspace
              </label>
              <select
                id="workspace-select"
                value={activeWorkspace?.id ?? ''}
                onChange={(event) => {
                  const next = workspaces.find((workspace) => workspace.id === event.target.value);
                  if (next !== undefined) navigate({ workspaceSlug: next.slug, boardId: null });
                }}
                className={`${inputClass} mb-2`}
              >
                {workspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </option>
                ))}
              </select>

              <div className="flex items-center justify-between px-1.5 pt-1 pb-1">
                <h2 className="text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">Boards</h2>
                {can(role, 'board:create') && (
                  <button
                    type="button"
                    onClick={() => setCreatingBoard(true)}
                    aria-label="Create a board"
                    className="rounded p-0.5 text-ink-subtle hover:bg-sunken hover:text-ink"
                  >
                    <Icon name="plus" size={13} />
                  </button>
                )}
              </div>

              {boards === null ? (
                <p className="px-2 py-2 text-xs text-ink-subtle">
                  <Spinner label="Loading boards" />
                </p>
              ) : boards.length === 0 ? (
                <p className="px-2 py-2 text-xs text-ink-subtle">No boards yet.</p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {boards.map((board) => {
                    const active = board.id === route.boardId;
                    return (
                      <li key={board.id}>
                        <button
                          type="button"
                          onClick={() => navigate({ workspaceSlug: activeWorkspace?.slug ?? null, boardId: board.id })}
                          aria-current={active ? 'page' : undefined}
                          className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-sm ${
                            active ? 'bg-accent-soft font-medium text-accent' : 'text-ink-muted hover:bg-sunken hover:text-ink'
                          }`}
                        >
                          {board.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="border-t border-border px-2 py-2">
          <div className="flex items-center gap-1">
            {(['light', 'dark'] as const).map((candidate) => (
              <button
                key={candidate}
                type="button"
                onClick={() => setPreference(candidate)}
                aria-pressed={preference === candidate}
                className={`flex-1 rounded-md px-2 py-1 text-[11px] ${
                  preference === candidate ? 'bg-sunken text-ink' : 'text-ink-subtle hover:text-ink'
                }`}
              >
                {candidate === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPreference('system')}
              aria-pressed={preference === 'system'}
              className={`rounded-md px-2 py-1 text-[11px] ${
                preference === 'system' ? 'bg-sunken text-ink' : 'text-ink-subtle hover:text-ink'
              }`}
            >
              Auto
            </button>
          </div>
        </div>
      </nav>

      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        {error !== null && (
          <p role="alert" className="border-b border-danger/30 bg-danger-soft px-4 py-2 text-xs text-danger">
            {error}
          </p>
        )}

        {activeWorkspace === null ? (
          <EmptyWorkspace />
        ) : route.boardId === null ? (
          <div className="flex flex-1 items-center justify-center text-sm text-ink-subtle">
            <Spinner label="Opening a board" />
          </div>
        ) : (
          // Keyed on the board id so that switching boards remounts the provider
          // rather than reusing one whose socket points at the previous board.
          <BoardProvider
            key={route.boardId}
            boardId={route.boardId}
            client={client}
            getAccessToken={getAccessToken}
            currentUser={user}
          >
            <BoardView
              currentUserId={user.id}
              role={role}
              onBoardDeleted={() => {
                void loadBoards();
                navigate({ workspaceSlug: activeWorkspace.slug, boardId: null });
              }}
            />
          </BoardProvider>
        )}
      </main>

      {creatingBoard && activeWorkspace !== null && (
        <CreateBoardDialog
          workspaceSlug={activeWorkspace.slug}
          onClose={() => setCreatingBoard(false)}
          onCreated={(board) => {
            setCreatingBoard(false);
            void loadBoards();
            navigate({ workspaceSlug: activeWorkspace.slug, boardId: board.id });
          }}
        />
      )}
    </div>
  );
}

function EmptyWorkspace(): ReactNode {
  const { client } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex flex-1 items-center justify-center px-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed === '') return;
          setBusy(true);
          setError(null);
          void client
            .createWorkspace({ name: trimmed })
            .then(() => window.location.reload())
            .catch((caught: unknown) => {
              setBusy(false);
              setError(caught instanceof ApiError ? caught.detail : 'The workspace could not be created.');
            });
        }}
        className="w-full max-w-sm rounded-xl border border-border bg-surface p-5"
      >
        <h2 className="text-sm font-semibold text-ink">Create your first workspace</h2>
        <p className="mt-1 text-xs text-ink-muted">
          A workspace owns boards, and membership in it is what grants access to them. You become its owner.
        </p>
        <label htmlFor="workspace-name" className="mt-4 block text-xs font-medium text-ink-muted">
          Workspace name
        </label>
        <input
          id="workspace-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className={`${inputClass} mt-1`}
        />
        {error !== null && <p className="mt-2 text-xs text-danger">{error}</p>}
        <button type="submit" disabled={busy || name.trim() === ''} className={`${primaryButtonClass} mt-4 w-full`}>
          {busy ? <Spinner label="Creating" /> : null}
          Create workspace
        </button>
      </form>
    </div>
  );
}

function CreateBoardDialog({
  workspaceSlug,
  onClose,
  onCreated,
}: {
  workspaceSlug: string;
  onClose(): void;
  onCreated(board: Board): void;
}): ReactNode {
  const { client } = useAuth();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title="New board" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed === '') return;
          setBusy(true);
          setError(null);
          void client
            .createBoard(workspaceSlug, { name: trimmed })
            .then((response) => {
              if (response.board !== null) onCreated(response.board);
              else onClose();
            })
            .catch((caught: unknown) => {
              setBusy(false);
              setError(caught instanceof ApiError ? caught.detail : 'The board could not be created.');
            });
        }}
      >
        <label htmlFor="board-name" className="block text-xs font-medium text-ink-muted">
          Board name
        </label>
        <input
          id="board-name"
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          className={`${inputClass} mt-1`}
        />
        <p className="mt-2 text-[11px] text-ink-subtle">
          Four columns are created with it: Backlog, In progress, In review, Done.
        </p>
        {error !== null && <p className="mt-2 text-xs text-danger">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy || name.trim() === ''} className={primaryButtonClass}>
            Create board
          </button>
        </div>
      </form>
    </Modal>
  );
}
